import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    CRMInstallationsGasApi,
    CRMInstallationsHeatPumpApi,
    CRMInstallationsMeterApi,
    CRMInstallationsPVBatteryApi,
    CRMInstallationsSmartHomeApi,
    CRMInstallationsWallBoxApi,
    DataImportAddImportDataBySiteIdRequest,
    DataImportApi,
    DataImportGetImportBySiteIdRequest,
    DataImportRetrieveModel,
    InstallationsGatewayApi,
} from '@swagger-http';
import {
    Configuration as DashboardConfiguration,
    PvBatteryApi,
    PvBatteryGetEfficiencyCheck1Request,
    PvBatteryGetEfficiencyCheckRequest,
} from '@swagger-http/dashboard';

import { createRequestConfiguration } from '@tools/utils/authentication';

import { DASHBOARD_API_URL_DEFAULT as dashboardApiUrl } from '@tools/constants';
import { Scope } from '@tools/enums';
import { checkForScopes } from '@tools/utils';

export const useInstallationsGatewayGetGatewayDevices = (
    id: string,
    enabled?: boolean,
) =>
    useQuery({
        queryKey: ['getGatewayDevices', id],
        enabled,
        queryFn: async () =>
            new InstallationsGatewayApi(
                createRequestConfiguration(),
            ).installationsGatewayGetGatewayDevices({ id }),
    });

export const usePvBatteryInstallationStatus = (
    customerId: string,
    enabled?: boolean,
) =>
    useQuery({
        queryKey: ['getPvBatteryInstallationStatus', customerId],
        enabled,
        queryFn: async () =>
            new CRMInstallationsPVBatteryApi(
                createRequestConfiguration(),
            ).crmInstallationsPvBatteryGetSmartEnergyDevices({
                id: customerId,
                useCache: 'false',
            }),
    });

export const usePvBatteryGetSmartEnergyInstallation = (
    customerId: string,
    installationId: string,
) =>
    useQuery({
        queryKey: ['pvBatteryGetSmartEnergyInstallation', customerId],
        queryFn: async () =>
            new CRMInstallationsPVBatteryApi(
                createRequestConfiguration(),
            ).crmInstallationsPvBatteryGetSmartEnergyDevice({
                id: customerId,
                installationId: installationId,
            }),
    });

export const useWallboxInstallationStatus = (
    customerId: string,
    enabled?: boolean,
) =>
    useQuery({
        queryKey: ['getWallboxInstallationStatus', customerId],
        enabled,
        queryFn: async () =>
            new CRMInstallationsWallBoxApi(
                createRequestConfiguration(),
            ).crmInstallationsWallBoxGetWallboxDevices({
                id: customerId,
                useCache: 'false',
            }),
    });

export const useSmartHomeInstallationStatus = (id: string, enabled?: boolean) =>
    useQuery({
        queryKey: ['getSmartHomeInstallationStatus', id],
        enabled,
        queryFn: async () =>
            new CRMInstallationsSmartHomeApi(
                createRequestConfiguration(),
            ).crmInstallationsSmartHomeGetSmartHomeInstallations({ id }),
    });

export const useGasMeterInstallationStatus = (id: string, enabled?: boolean) =>
    useQuery({
        queryKey: ['getGasMeterInstallationStatus', id],
        enabled,
        queryFn: async () =>
            new CRMInstallationsGasApi(
                createRequestConfiguration(),
            ).crmInstallationsGasGetMeterDevices({ id }),
    });

export const useSmartMeterInstallationStatus = (
    id: string,
    enabled?: boolean,
) =>
    useQuery({
        queryKey: ['getSmartMeterInstallationStatus', id],
        enabled,
        queryFn: async () =>
            await new CRMInstallationsMeterApi(
                createRequestConfiguration(),
            ).crmInstallationsMeterGetMeterDevices({ id, useCache: 'false' }),
    });

const getApiV2PvBatteryEfficiencyCheck = (
    requestParams: PvBatteryGetEfficiencyCheck1Request,
    customerToken: string,
) =>
    new PvBatteryApi(
        createRequestConfiguration(
            dashboardApiUrl,
            customerToken,
        ) as unknown as DashboardConfiguration,
    ).pvBatteryGetEfficiencyCheck1(requestParams);

export const usePvBatteryGetEfficiencyCheck = (
    dateTimeRange: PvBatteryGetEfficiencyCheckRequest,
    token: string,
    enabled?: boolean,
) =>
    useQuery({
        queryKey: [
            'getPvBatteryGetEfficiencyCheck',
            JSON.stringify(dateTimeRange),
        ],
        enabled,
        queryFn: async () =>
            await getApiV2PvBatteryEfficiencyCheck(dateTimeRange, token),
    });

export const useGetDataImportAddImportBySiteId = (
    value: DataImportGetImportBySiteIdRequest,
    enabled?: boolean,
) => {
    const hasGetDataImportPermissions = checkForScopes([
        Scope.DATA_IMPORT_READ,
    ]);

    return useQuery({
        queryKey: ['getDataImportAddImportBySiteId', value.siteId],
        enabled,
        queryFn: async () => {
            if (!hasGetDataImportPermissions) {
                throw new Error(
                    `current user has no permission ${Scope.DATA_IMPORT_READ}`,
                );
            }

            return await new DataImportApi(
                createRequestConfiguration(),
            ).dataImportGetImportBySiteId(value);
        },
    });
};

export const useDataImportAddImportBySiteId = (
    siteId: string,
    retry?: boolean,
) => {
    const queryClient = useQueryClient();

    const hasTriggerDataImportPermissions = checkForScopes([
        Scope.DATA_IMPORT_WRITE,
    ]);

    return useMutation({
        mutationKey: ['dataImportGetImportDataBySiteId', siteId],
        retry,
        mutationFn: async (data: DataImportRetrieveModel) => {
            if (!hasTriggerDataImportPermissions) {
                throw new Error(
                    `current user has no permission ${Scope.DATA_IMPORT_WRITE}`,
                );
            }

            const value: DataImportAddImportDataBySiteIdRequest = {
                siteId,
                dataImportRetrieveModel: data,
            };

            return await new DataImportApi(
                createRequestConfiguration(),
            ).dataImportAddImportDataBySiteId(value);
        },
        onSuccess: async () => {
            queryClient.invalidateQueries({
                queryKey: ['getDataImportAddImportBySiteId', siteId],
            });
        },
    });
};

export const useHeatPumpInstallationStatus = (
    customerId: string,
    enabled?: boolean,
) =>
    useQuery({
        queryKey: ['getHeatPumpInstallationStatus', customerId],
        enabled,
        queryFn: async () =>
            new CRMInstallationsHeatPumpApi(
                createRequestConfiguration(),
            ).crmInstallationsHeatPumpGetHeatPumpDevices({
                id: customerId,
            }),
    });
